import { emptyActivity, reduceActivity } from './activity.js';
import { parseCommand } from './lib/summarize.js';
import { emptyProgress, reduceProgress } from './progress.js';
/**
 * Transcript view model.
 *
 * `TranscriptView` is what every viewer component renders. It is built by a pure-ish reducer
 * (`reduceTranscript`) from normalized protocol events (see ./protocol.ts). Components never
 * look at raw events.
 *
 *   events ──adaptEvent──▶ reduceTranscript ──▶ TranscriptView
 *                                                 └─ entries: (turn | interstitial marker)[]
 *                                                      └─ turn.items: TurnItem[] (subagents nest)
 *
 * Disclosure (docs/PLAN.md → Conversation viewer):
 *   L0 — user message + final answer + one summary row; questions, approvals, errors pinned.
 *   L1 — activity chips/rows for every tool (consecutive reads/searches grouped), text, thinking
 *        collapsed.
 *   L2 — everything expanded (full tool output, thinking text).
 */
import { adaptEvent, } from './protocol.js';
export const ITEM_STATES = [
    'pending',
    'streaming',
    'ok',
    'error',
    'cancelled',
    'awaiting-user',
    'stale',
];
/** A live item shown while the client is disconnected is rendered as stale, not "running". */
export function effectiveState(state, connection) {
    if (connection === 'live')
        return state;
    return state === 'streaming' || state === 'pending' ? 'stale' : state;
}
// ---------------------------------------------------------------------------------------------
// Construction
// ---------------------------------------------------------------------------------------------
export function emptySummary() {
    return {
        tools: 0,
        commands: 0,
        edits: 0,
        reads: 0,
        searches: 0,
        web: 0,
        mcp: 0,
        subagents: 0,
        images: 0,
        files: 0,
        additions: 0,
        deletions: 0,
        errors: 0,
    };
}
export function createTranscript(runId = '', agent = 'claude') {
    return {
        progress: emptyProgress(),
        activity: emptyActivity(),
        runId,
        agent,
        session: { runId, agent },
        status: 'idle',
        connection: 'live',
        entries: [],
        todos: null,
        usage: null,
        ratelimits: [],
        awaiting: 0,
        queued: [],
        lastSeq: 0,
        lastTs: 0,
        refs: new Map(),
    };
}
/** Build a view from a full event list (replay / import). */
export function buildTranscript(events, connection = 'live') {
    let v = createTranscript();
    for (const e of events)
        v = reduceTranscript(v, adaptEvent(e));
    if (connection !== 'live')
        v = reduceTranscript(v, { kind: 'client.connection', state: connection });
    return v;
}
export function todoEntries(e) {
    return e.items.map((i, n) => ({
        id: i.id ?? String(n),
        text: i.text,
        status: i.status,
        activeForm: i.activeForm,
    }));
}
/** Pure header step (the caller drops duplicates at or below `lastSeq`). */
export function reduceHeader(h, e) {
    const v = {
        progress: reduceProgress(h.progress, e),
        activity: reduceActivity(h.activity, e),
        runId: h.runId || e.runId,
        agent: !h.runId ? e.agent : h.agent,
        session: h.runId ? h.session : { ...h.session, runId: e.runId, agent: e.agent },
        status: h.status,
        statusDetail: h.statusDetail,
        todos: h.todos,
        usage: h.usage,
        ratelimits: h.ratelimits,
        ratelimitAt: h.ratelimitAt,
        queued: h.queued,
        lastSeq: Math.max(h.lastSeq, e.seq),
        lastTs: Math.max(h.lastTs, e.ts),
    };
    switch (e.kind) {
        case 'run.start':
            v.session = {
                ...v.session,
                nativeSessionId: e.nativeSessionId,
                model: e.model,
                ...(e.models?.length ? { models: e.models } : {}),
                effort: e.effort,
                ...(e.modelReason ? { modelReason: e.modelReason } : {}),
                cwd: e.cwd,
                cliVersion: e.cliVersion,
                permissionMode: e.permissionMode,
                resumed: e.resumed,
                startedAt: e.ts,
            };
            v.status = 'running';
            break;
        case 'status':
            v.status = e.state;
            v.statusDetail = e.detail;
            break;
        case 'usage': {
            if (e.parentId) {
                // A subagent's own meter: recorded as a breakdown, never as the session's totals.
                const total = e.cumulative
                    ? (e.cumulative.total ??
                        e.cumulative.input +
                            e.cumulative.output +
                            e.cumulative.cacheRead +
                            e.cumulative.cacheWrite)
                    : undefined;
                const children = v.usage?.children ?? { byParent: {}, byTask: {} };
                const turnTotal = e.turn
                    ? (e.turn.total ?? e.turn.input + e.turn.output + e.turn.cacheRead + e.turn.cacheWrite)
                    : 0;
                v.usage = {
                    ...v.usage,
                    children: {
                        ...children,
                        byParent: {
                            ...children.byParent,
                            [e.parentId]: total ?? (children.byParent[e.parentId] ?? 0) + turnTotal,
                        },
                    },
                };
                break;
            }
            v.usage = {
                ...v.usage,
                contextTokens: e.contextTokens ?? v.usage?.contextTokens,
                contextWindow: e.contextWindow ?? v.usage?.contextWindow,
                contextPct: e.contextPct ??
                    (e.contextTokens && (e.contextWindow ?? v.usage?.contextWindow)
                        ? (100 * e.contextTokens) / (e.contextWindow ?? v.usage?.contextWindow ?? 1)
                        : v.usage?.contextPct),
                costUsd: e.costUsd ?? v.usage?.costUsd,
                cumulative: e.cumulative ?? v.usage?.cumulative,
                observedAt: e.ts,
            };
            if (e.model)
                v.session = { ...v.session, model: e.model };
            break;
        }
        case 'ratelimit':
            v.ratelimits = e.windows.map((w) => ({ ...w }));
            v.ratelimitAt = e.ts;
            break;
        case 'turn.end':
            if (v.status === 'running')
                v.status = 'idle';
            break;
        case 'user.msg':
            if (e.queued && e.messageId) {
                const item = {
                    type: 'user',
                    id: e.messageId,
                    seq: e.seq,
                    ts: e.ts,
                    state: 'ok',
                    text: e.text,
                    content: e.content ?? [],
                    queued: true,
                    origin: e.origin ?? 'user',
                };
                v.queued = [...v.queued, { messageId: e.messageId, text: e.text, ts: e.ts, item }];
            }
            break;
        case 'user.msg.status': {
            const q = v.queued.find((m) => m.messageId === e.messageId);
            if (q && (e.state === 'cancelled' || e.state === 'started'))
                v.queued = v.queued.filter((m) => m !== q);
            break;
        }
        case 'todo':
            if (!e.parentId)
                v.todos = todoEntries(e);
            break;
        case 'compaction':
            if (v.status === 'compacting')
                v.status = 'running';
            break;
        case 'marker':
            if (e.marker === 'title' && e.text)
                v.session = { ...v.session, agentTitle: e.text };
            if (e.marker === 'model_change' && e.to) {
                // g18: a switched model is no longer Viko's pick, so its reason no longer applies.
                const { modelReason: _stale, ...session } = v.session;
                v.session = { ...session, model: e.to };
            }
            break;
        case 'error':
            if (e.fatal)
                v.status = 'error';
            break;
        case 'cli.task':
            if (e.usage?.totalTokens !== undefined) {
                const children = v.usage?.children ?? { byParent: {}, byTask: {} };
                v.usage = {
                    ...v.usage,
                    children: {
                        ...children,
                        byTask: {
                            ...children.byTask,
                            [e.taskId]: Math.max(children.byTask[e.taskId] ?? 0, e.usage.totalTokens),
                        },
                    },
                };
            }
            break;
    }
    return v;
}
export const IMPLICIT_TURN = '__implicit__';
export function artifactItemOf(e) {
    return {
        type: 'artifact',
        id: e.artifactId,
        seq: e.seq,
        ts: e.ts,
        state: 'ok',
        artifactId: e.artifactId,
        encrypted: e.encrypted,
        thumbEncrypted: e.thumbEncrypted,
        thumbArtifactId: e.thumbArtifactId,
        name: e.name,
        mime: e.mime,
        path: e.path,
        url: e.url,
        thumbUrl: e.thumbUrl,
        bytes: e.bytes,
        caption: e.caption,
        toolCallId: e.toolCallId,
        // ---- auto-artifacts ----
        source: e.source,
        collection: e.collection,
        external: e.external,
        bundle: e.bundle,
        offered: e.offered,
        // ---- end auto-artifacts ----
        shareRoot: e.shareRoot, // agent-sharing
    };
}
/** A later event for a known artifact updates it in place (first seq/ts kept). */
export function mergeArtifact(previous, item) {
    return { ...previous, ...definedOnly(item), seq: previous.seq, ts: previous.ts };
}
export function reduceTranscript(view, input) {
    if (input.kind === 'client.connection') {
        return input.state === view.connection ? view : { ...view, connection: input.state };
    }
    if (input.kind === 'client.reset')
        return createTranscript(view.runId, view.agent);
    const e = input;
    // Replays after reconnect re-send from a cursor; anything at or below lastSeq is a duplicate.
    if (view.runId && e.runId === view.runId && e.seq <= view.lastSeq)
        return view;
    // Run-level fields come from the shared header reducer (the daemon's materialized state runs
    // the same code); everything below only places items into turns.
    let v = { ...view, ...reduceHeader(view, e) };
    switch (e.kind) {
        case 'progress':
        case 'run.start':
        case 'status':
        case 'ratelimit':
        case 'call.frame': // viko-call: the call panel renders these
            return v;
        case 'usage':
            if (!e.parentId && e.turnId && e.turn) {
                const turn = e.turn;
                v = withTurn(v, e, (t) => ({ ...t, usage: turn }), false);
            }
            return v;
        case 'turn.start':
            return withTurn(v, e, (t) => t, true);
        case 'turn.end':
            v = withTurn(v, e, (t) => ({
                ...t,
                outcome: e.outcome,
                endedAt: e.ts,
                durationMs: e.durationMs ?? e.ts - t.startedAt,
                error: e.error,
                stopReason: e.stopReason,
                resultText: e.resultText,
                items: closeOpen(t.items, e.outcome === 'ok' ? 'ok' : 'cancelled', e.ts),
            }), false);
            return v;
        case 'user.msg': {
            const item = {
                type: 'user',
                id: e.messageId ?? e.eventId,
                seq: e.seq,
                ts: e.ts,
                state: 'ok',
                text: e.text,
                content: e.content ?? [],
                queued: e.queued ?? false,
                origin: e.origin ?? 'user',
            };
            // Queued messages wait in the composer until user.msg.status=started places them.
            if (e.queued && e.messageId)
                return v;
            return withTurn(v, e, (t) => (t.user || e.parentId ? appendItem(t, e.parentId, item) : { ...t, user: item }), true);
        }
        case 'user.msg.status': {
            // The header reducer already dequeued it; the item comes from the queue before the event.
            const q = view.queued.find((m) => m.messageId === e.messageId);
            if (!q || e.state !== 'started')
                return v;
            const item = { ...q.item, seq: e.seq };
            return withTurn(v, e, (t) => (t.user ? appendItem(t, e.parentId, item) : { ...t, user: item }), true);
        }
        case 'asst.delta':
        case 'asst.block':
            return withTurn(v, e, (t) => applyBlock(t, e), true);
        case 'tool.call':
            v.refs.set(e.toolCallId, resolveTurnId(v, e));
            return withTurn(v, e, (t) => appendItem(t, e.parentId, toolFromCall(e)), true);
        case 'tool.output':
            return withTurn(v, e, (t) => updateById(t, e.toolCallId, (it) => {
                if (it.type !== 'tool')
                    return it;
                if (e.stream === 'progress')
                    return { ...it, progress: e.text };
                return { ...it, output: it.output + e.text, state: 'streaming' };
            }), false, e.toolCallId);
        case 'tool.result':
            return withTurn(v, e, (t) => applyToolResult(t, e), false, e.toolCallId);
        case 'plan': {
            v.refs.set(e.planId, resolveTurnId(v, e));
            const item = {
                type: 'plan',
                id: e.planId,
                seq: e.seq,
                ts: e.ts,
                state: 'ok',
                planId: e.planId,
                text: e.text,
                filePath: e.filePath,
                toolCallId: e.toolCallId,
            };
            // The ExitPlanMode tool row is represented by the plan itself.
            return withTurn(v, e, (t) => appendItem(e.toolCallId ? removeById(t, e.toolCallId) : t, e.parentId, item), true);
        }
        case 'todo': {
            const items = todoEntries(e);
            const item = {
                type: 'todo',
                id: e.eventId,
                seq: e.seq,
                ts: e.ts,
                state: 'ok',
                items,
                explanation: e.explanation,
            };
            // A TodoWrite tool call is represented by this item; drop the duplicate tool row.
            return withTurn(v, e, (t) => {
                const base = e.toolCallId ? removeById(t, e.toolCallId) : t;
                return appendItem(base, e.parentId, item);
            }, true);
        }
        case 'diff.turn':
            return withTurn(v, e, (t) => ({ ...t, diff: e.files }), true);
        case 'question': {
            v.refs.set(e.questionId, resolveTurnId(v, e));
            const item = {
                type: 'question',
                id: e.questionId,
                seq: e.seq,
                ts: e.ts,
                state: 'awaiting-user',
                questionId: e.questionId,
                questions: e.questions,
                blocking: e.blocking,
                ...(e.decisionId ? { decisionId: e.decisionId } : {}),
            };
            v = withTurn(v, e, (t) => appendItem(e.toolCallId ? removeById(t, e.toolCallId) : t, e.parentId, item), true);
            return recountAwaiting(v);
        }
        case 'question.answered':
            v = withTurn(v, e, (t) => updateById(t, e.questionId, (it) => it.type === 'question'
                ? {
                    ...it,
                    answers: e.answers,
                    notes: e.notes,
                    by: e.by,
                    state: e.by === 'cancelled' ? 'cancelled' : 'ok',
                }
                : it), false, e.questionId);
            return recountAwaiting(v);
        // ---- phase2-decisions: thread messages and option revisions attach to the question card ----
        case 'question.thread':
            return withTurn(v, e, (t) => updateById(t, e.questionId, (it) => it.type === 'question' && !(it.thread ?? []).some((m) => m.id === e.messageId)
                ? {
                    ...it,
                    ...(!it.decisionId && e.decisionId ? { decisionId: e.decisionId } : {}),
                    thread: [
                        ...(it.thread ?? []),
                        {
                            id: e.messageId,
                            author: e.author,
                            ts: e.ts,
                            ...(e.text ? { text: e.text } : {}),
                            ...(e.delivery ? { delivery: e.delivery } : {}),
                            ...(e.attachments?.length ? { attachments: e.attachments } : {}),
                        },
                    ],
                }
                : it), false, e.questionId);
        case 'question.revised':
            v = withTurn(v, e, (t) => updateById(t, e.questionId, (it) => it.type === 'question'
                ? {
                    ...it,
                    ...(!it.decisionId && e.decisionId ? { decisionId: e.decisionId } : {}),
                    optionsRevision: e.optionsRevision,
                    ...(e.withdrawn ? { withdrawn: true, state: 'cancelled' } : {}),
                    revisions: (it.revisions ?? [])
                        .filter((r) => r.revision !== e.optionsRevision)
                        .concat({
                        revision: e.optionsRevision,
                        ts: e.ts,
                        // Before = the previous revision's set, else the asked options.
                        before: it.revisions?.at(-1)?.after ??
                            (it.questions[0]?.options ?? []).map((o) => ({ label: o.label })),
                        ...(e.options ? { after: e.options } : {}),
                        ...(e.withdrawn ? { withdrawn: true } : {}),
                        ...(e.note ? { note: e.note } : {}),
                    }),
                }
                : it), false, e.questionId);
            return recountAwaiting(v);
        // ---- end phase2-decisions ----
        case 'approval': {
            v.refs.set(e.approvalId, resolveTurnId(v, e));
            const item = {
                type: 'approval',
                id: e.approvalId,
                seq: e.seq,
                ts: e.ts,
                state: 'awaiting-user',
                approvalId: e.approvalId,
                approvalKind: e.approvalKind,
                toolCallId: e.toolCallId,
                toolName: e.toolName,
                input: e.input,
                title: e.title,
                command: e.command,
                cwd: e.cwd,
                reason: e.reason,
                diffs: e.diffs ?? [],
                plan: e.plan,
            };
            v = withTurn(v, e, (t) => absorbPlan(appendItem(t, e.parentId, item), item), true);
            return recountAwaiting(v);
        }
        case 'approval.resolved':
            v = withTurn(v, e, (t) => {
                const ap = findById(t.items, e.approvalId);
                const tool = ap?.type === 'approval' && ap.toolCallId ? findById(t.items, ap.toolCallId) : undefined;
                if (ap?.type === 'approval' &&
                    tool?.type === 'tool' &&
                    ap.approvalKind !== 'plan' &&
                    (e.decision === 'allow' || e.decision === 'allow_always')) {
                    const folded = updateById(t, tool.toolCallId, (it) => it.type === 'tool' ? { ...it, approval: { decision: e.decision, by: e.by } } : it);
                    return removeById(folded, e.approvalId);
                }
                return updateById(t, e.approvalId, (it) => it.type === 'approval'
                    ? {
                        ...it,
                        decision: e.decision,
                        message: e.message,
                        by: e.by,
                        state: e.decision === 'deny' ? 'error' : e.decision === 'cancel' ? 'cancelled' : 'ok',
                    }
                    : it);
            }, false, e.approvalId);
            return recountAwaiting(v);
        case 'compaction': {
            const item = {
                type: 'compaction',
                id: e.eventId,
                seq: e.seq,
                ts: e.ts,
                state: 'ok',
                trigger: e.trigger,
                preTokens: e.preTokens,
                postTokens: e.postTokens,
                summary: e.summary,
                durationMs: e.durationMs,
            };
            return placeInterstitial(v, e, item);
        }
        case 'marker': {
            const item = {
                type: 'marker',
                id: e.eventId,
                seq: e.seq,
                ts: e.ts,
                state: 'ok',
                marker: e.marker,
                text: e.text,
                from: e.from,
                to: e.to,
                ref: e.ref,
            };
            if (e.marker === 'title')
                return v;
            return placeInterstitial(v, e, item);
        }
        case 'notice': {
            const item = {
                type: 'notice',
                id: e.eventId,
                seq: e.seq,
                ts: e.ts,
                state: 'ok',
                level: e.level,
                text: e.text,
                code: e.code,
                ...(e.usageLimit ? { usageLimit: e.usageLimit } : {}),
            };
            if (e.usageLimit)
                v.usageLimit = e.usageLimit;
            return placeInterstitial(v, e, item);
        }
        case 'error': {
            const item = {
                type: 'error',
                id: e.eventId,
                seq: e.seq,
                ts: e.ts,
                state: e.retrying ? 'pending' : 'error',
                message: e.message,
                code: e.code,
                retrying: e.retrying ?? false,
                fatal: e.fatal ?? false,
            };
            return placeInterstitial(v, e, item);
        }
        case 'artifact': {
            const refId = `artifact:${e.artifactId}`;
            const known = v.refs.has(refId);
            const item = artifactItemOf(e);
            if (known)
                return withTurn(v, e, (t) => updateById(t, e.artifactId, (previous) => previous.type === 'artifact' ? mergeArtifact(previous, item) : previous), false, refId);
            v.refs.set(refId, resolveTurnId(v, e));
            return withTurn(v, e, (t) => appendItem(t, e.parentId, item), true, refId);
        }
        case 'hook': {
            const hook = {
                type: 'hook',
                id: e.hookId,
                seq: e.seq,
                ts: e.ts,
                state: e.phase !== 'completed'
                    ? 'streaming'
                    : e.outcome === 'error' || e.outcome === 'blocked'
                        ? 'error'
                        : e.outcome === 'cancelled'
                            ? 'cancelled'
                            : 'ok',
                hookId: e.hookId,
                hookName: e.hookName,
                hookEvent: e.hookEvent,
                phase: e.phase,
                outcome: e.outcome,
                output: [e.output, e.stdout, e.stderr].filter(Boolean).join('\n') || undefined,
                exitCode: e.exitCode,
            };
            if (!e.turnId)
                return v;
            return withTurn(v, e, (t) => {
                if (e.toolCallId && findById(t.items, e.toolCallId)) {
                    return updateById(t, e.toolCallId, (it) => it.type === 'tool' ? { ...it, hooks: upsert(it.hooks, hook) } : it);
                }
                return findById(t.items, hook.id)
                    ? updateById(t, hook.id, () => hook)
                    : appendItem(t, e.parentId, hook);
            }, true);
        }
        case 'cli.task': {
            const task = {
                taskId: e.taskId,
                state: e.state,
                summary: e.summary,
                lastTool: e.lastTool,
                description: e.description,
            };
            const tcid = e.toolCallId;
            const turnId = (tcid && v.refs.get(tcid)) || e.turnId || lastTurnId(v);
            if (!turnId)
                return v;
            return withTurn(v, { ...e, turnId }, (t) => {
                if (tcid && findById(t.items, tcid)) {
                    return updateById(t, tcid, (it) => applyTask(it, e, task));
                }
                const item = {
                    type: 'task',
                    id: e.taskId,
                    seq: e.seq,
                    ts: e.ts,
                    state: e.state === 'failed'
                        ? 'error'
                        : e.state === 'stopped'
                            ? 'cancelled'
                            : e.state === 'completed'
                                ? 'ok'
                                : 'streaming',
                    task,
                    taskType: e.taskType,
                    background: e.background ?? false,
                };
                return findById(t.items, item.id)
                    ? updateById(t, item.id, () => item)
                    : appendItem(t, e.parentId, item);
            }, true);
        }
        case 'raw': {
            const item = {
                type: 'raw',
                id: e.eventId,
                seq: e.seq,
                ts: e.ts,
                state: 'ok',
                source: e.source,
                data: e.data,
            };
            return placeInterstitial(v, e, item);
        }
    }
}
function newTurn(id, e, index) {
    return {
        id,
        index,
        agent: e.agent,
        state: 'streaming',
        startedAt: e.ts,
        user: null,
        items: [],
        diff: null,
        summary: emptySummary(),
    };
}
/**
 * Apply `fn` to the turn an event belongs to (creating it when `create`), then re-derive the
 * turn's state and summary. `refId` lets result-style events find their turn through the refs
 * index when they arrive without a turnId.
 */
function withTurn(v, e, fn, create, refId) {
    const id = resolveTurnId(v, e, refId);
    let idx = findTurnIndex(v, id);
    let entries = v.entries;
    if (idx < 0) {
        if (!create)
            return v;
        const count = entries.reduce((n, x) => n + (x.kind === 'turn' ? 1 : 0), v.turnBase ?? 0);
        entries = [...entries, { kind: 'turn', id, turn: newTurn(id, e, count + 1) }];
        idx = entries.length - 1;
        v.refs.set(id, id);
    }
    const entry = entries[idx];
    if (entry?.kind !== 'turn')
        return v;
    const next = finalizeTurn(fn(entry.turn));
    if (next === entry.turn && entries === v.entries)
        return v;
    const copy = entries === v.entries ? [...entries] : entries;
    copy[idx] = { kind: 'turn', id, turn: next };
    return { ...v, entries: copy };
}
/**
 * Which turn an event lands in. Updates to a known item follow the item (a background
 * subagent launched in turn 1 keeps reporting during turn 2); nested events follow their
 * parent subagent; everything else uses its own turnId.
 */
function resolveTurnId(v, e, refId) {
    return ((refId ? v.refs.get(refId) : undefined) ??
        (e.parentId ? v.refs.get(e.parentId) : undefined) ??
        e.turnId ??
        lastTurnId(v) ??
        IMPLICIT_TURN);
}
function definedOnly(o) {
    return Object.fromEntries(Object.entries(o).filter(([, x]) => x !== undefined));
}
function lastTurnId(v) {
    for (let i = v.entries.length - 1; i >= 0; i--) {
        const x = v.entries[i];
        if (x?.kind === 'turn')
            return x.id;
    }
    return undefined;
}
function findTurnIndex(v, id) {
    for (let i = v.entries.length - 1; i >= 0; i--) {
        const x = v.entries[i];
        if (x?.kind === 'turn' && x.id === id)
            return i;
    }
    return -1;
}
/** Markers/notices inside a turn go into the turn; outside a turn they sit between turns. */
function placeInterstitial(v, e, item) {
    if (e.turnId && findTurnIndex(v, resolveTurnId(v, e)) >= 0) {
        return withTurn(v, e, (t) => appendItem(t, e.parentId, item), false);
    }
    return { ...v, entries: [...v.entries, { kind: 'interstitial', id: item.id, item }] };
}
function appendItem(t, parentId, item) {
    if (parentId) {
        let placed = false;
        const items = mapTree(t.items, (it) => {
            if (it.type === 'subagent' && it.toolCallId === parentId) {
                placed = true;
                const inner = [...it.items, item];
                return { ...it, items: inner, summary: summarize(inner, null) };
            }
            return it;
        });
        if (placed)
            return { ...t, items };
    }
    return { ...t, items: [...t.items, item] };
}
/** Map over items, descending into subagents; returns the same array when nothing changed. */
function mapTree(items, fn) {
    let changed = false;
    const out = items.map((it) => {
        let next = fn(it);
        if (next === it && it.type === 'subagent') {
            const inner = mapTree(it.items, fn);
            if (inner !== it.items)
                next = { ...it, items: inner, summary: summarize(inner, null) };
        }
        if (next !== it)
            changed = true;
        return next;
    });
    return changed ? out : items;
}
function itemKey(it) {
    switch (it.type) {
        case 'tool':
        case 'subagent':
            return it.toolCallId;
        case 'question':
            return it.questionId;
        case 'approval':
            return it.approvalId;
        case 'text':
        case 'thinking':
            return it.blockId;
        default:
            return it.id;
    }
}
export function findById(items, id) {
    for (const it of items) {
        if (itemKey(it) === id)
            return it;
        if (it.type === 'subagent') {
            const hit = findById(it.items, id);
            if (hit)
                return hit;
        }
    }
    return undefined;
}
function updateById(t, id, fn) {
    const items = mapTree(t.items, (it) => (itemKey(it) === id ? fn(it) : it));
    return items === t.items ? t : { ...t, items };
}
function removeById(t, id) {
    const strip = (items) => items
        .filter((it) => itemKey(it) !== id)
        .map((it) => (it.type === 'subagent' ? { ...it, items: strip(it.items) } : it));
    return findById(t.items, id) ? { ...t, items: strip(t.items) } : t;
}
function upsert(list, item) {
    const i = list.findIndex((x) => x.id === item.id);
    if (i < 0)
        return [...list, item];
    const copy = [...list];
    copy[i] = item;
    return copy;
}
// ---------------------------------------------------------------------------------------------
// Event-specific transforms
// ---------------------------------------------------------------------------------------------
function applyBlock(t, e) {
    const existing = findById(t.items, e.blockId);
    const isBlock = e.kind === 'asst.block';
    if (existing && (existing.type === 'text' || existing.type === 'thinking')) {
        // A delta that arrives after the final block is stale; the block always wins.
        if (!isBlock && !existing.streaming)
            return t;
        return updateById(t, e.blockId, (it) => {
            if (it.type !== 'text' && it.type !== 'thinking')
                return it;
            const text = isBlock ? e.text : it.text + e.text;
            const done = isBlock;
            const base = {
                ...it,
                text,
                streaming: !done,
                state: (done ? 'ok' : 'streaming'),
            };
            if (it.type === 'thinking') {
                return {
                    ...base,
                    type: 'thinking',
                    redacted: isBlock ? (e.redacted ?? false) : it.redacted,
                    durationMs: done ? e.ts - it.ts : undefined,
                };
            }
            return {
                ...base,
                type: 'text',
                aborted: isBlock ? (e.aborted ?? false) : false,
                phase: isBlock ? e.phase : it.phase,
            };
        });
    }
    const common = {
        id: e.blockId,
        blockId: e.blockId,
        seq: e.seq,
        ts: e.ts,
        text: e.text,
        streaming: !isBlock,
        state: (isBlock ? 'ok' : 'streaming'),
    };
    const item = e.blockType === 'thinking'
        ? {
            ...common,
            type: 'thinking',
            redacted: isBlock ? (e.redacted ?? false) : false,
            durationMs: undefined,
        }
        : {
            ...common,
            type: 'text',
            aborted: isBlock ? (e.aborted ?? false) : false,
            phase: isBlock ? e.phase : undefined,
        };
    return appendItem(t, e.parentId, item);
}
function toolFromCall(e) {
    const base = {
        id: e.toolCallId,
        toolCallId: e.toolCallId,
        seq: e.seq,
        ts: e.ts,
        state: 'pending',
        background: e.background ?? false,
    };
    if (e.toolKind === 'subagent') {
        const input = (e.input ?? {});
        return {
            ...base,
            type: 'subagent',
            state: 'streaming',
            agentType: e.subagent?.type,
            description: e.subagent?.description ?? e.title ?? input.description ?? 'Subagent',
            prompt: input.prompt,
            items: [],
            summary: emptySummary(),
        };
    }
    return {
        ...base,
        type: 'tool',
        state: 'streaming',
        name: e.name,
        toolKind: e.toolKind,
        title: e.title ?? e.command ?? e.path ?? e.name,
        input: e.input,
        command: e.command,
        cwd: e.cwd,
        path: e.path,
        mcp: e.mcp,
        output: '',
        diffs: e.diffs ?? [],
        images: [],
        hooks: [],
    };
}
function applyToolResult(t, e) {
    const state = e.status === 'ok'
        ? 'ok'
        : e.status === 'interrupted'
            ? 'cancelled'
            : e.status === 'denied'
                ? 'cancelled'
                : 'error';
    return updateById(t, e.toolCallId, (it) => {
        if (it.type === 'tool') {
            return {
                ...it,
                state: it.background && e.status === 'ok' && !it.task ? 'ok' : state,
                result: {
                    status: e.status,
                    output: e.output,
                    content: e.content,
                    exitCode: e.exitCode,
                    structured: e.structured,
                },
                diffs: e.diffs?.length ? e.diffs : it.diffs,
                images: [
                    ...it.images,
                    ...(e.images ?? []),
                    ...(e.content ?? []).flatMap((c) => (c.type === 'image' ? [c.image] : [])),
                ],
                durationMs: e.durationMs ?? e.ts - it.ts,
                endedAt: e.ts,
            };
        }
        if (it.type === 'subagent') {
            // Background agents return "launched" immediately; cli.task carries the real outcome.
            if (it.background && e.status === 'ok')
                return it;
            return {
                ...it,
                state,
                result: e.output,
                durationMs: e.durationMs ?? e.ts - it.ts,
                endedAt: e.ts,
                items: closeOpen(it.items, state === 'ok' ? 'ok' : 'cancelled', e.ts),
            };
        }
        return it;
    });
}
/** cli.task lifecycle for the tool/subagent it belongs to (background shells and agents). */
function applyTask(it, e, task) {
    const ended = e.state === 'completed' || e.state === 'failed' || e.state === 'stopped';
    const endState = e.state === 'failed' ? 'error' : e.state === 'stopped' ? 'cancelled' : 'ok';
    if (it.type === 'subagent') {
        const background = it.background || (e.background ?? false);
        const merged = { ...it.task, ...definedOnly(task) };
        if (!ended)
            return { ...it, task: merged, background, state: 'streaming' };
        return {
            ...it,
            task: merged,
            background,
            state: endState,
            result: e.summary ?? it.result,
            endedAt: e.ts,
            durationMs: e.ts - it.ts,
            items: closeOpen(it.items, endState === 'ok' ? 'ok' : 'cancelled', e.ts, true),
        };
    }
    if (it.type === 'tool') {
        const background = it.background || (e.background ?? false);
        const merged = { ...it.task, ...definedOnly(task) };
        if (!ended)
            return { ...it, task: merged, background };
        // A foreground shell's result decides its state; a background one ends with its task.
        return {
            ...it,
            task: merged,
            background,
            state: it.result && !background ? it.state : endState,
        };
    }
    return it;
}
/** A plan approval absorbs the matching plan item so the plan renders once, inside the card. */
function absorbPlan(t, approval) {
    if (approval.approvalKind !== 'plan')
        return t;
    const plan = t.items.find((it) => it.type === 'plan' && (approval.toolCallId ? it.toolCallId === approval.toolCallId : true));
    const base = approval.toolCallId ? removeById(t, approval.toolCallId) : t;
    if (!plan)
        return base;
    return updateById(removeById(base, plan.id), approval.approvalId, (it) => it.type === 'approval' ? { ...it, plan: it.plan ?? plan.text } : it);
}
/** On turn end, anything still running is closed (ok) or marked cancelled (interrupt/error). */
function closeOpen(items, to, ts, force = false) {
    return mapTree(items, (it) => {
        if (it.state !== 'streaming' && it.state !== 'pending')
            return it;
        if (!force && (it.type === 'tool' || it.type === 'subagent') && it.background)
            return it;
        if (it.type === 'text' || it.type === 'thinking')
            return { ...it, streaming: false, state: 'ok' };
        if (it.type === 'tool' || it.type === 'subagent') {
            return { ...it, state: to, endedAt: it.endedAt ?? ts };
        }
        return { ...it, state: to };
    });
}
function recountAwaiting(v) {
    let n = 0;
    const walk = (items) => {
        for (const it of items) {
            if ((it.type === 'question' || it.type === 'approval') && it.state === 'awaiting-user')
                n++;
            if (it.type === 'subagent')
                walk(it.items);
        }
    };
    for (const x of v.entries)
        if (x.kind === 'turn')
            walk(x.turn.items);
    return n === v.awaiting ? v : { ...v, awaiting: n };
}
// ---------------------------------------------------------------------------------------------
// Derivations
// ---------------------------------------------------------------------------------------------
function finalizeTurn(t) {
    const summary = summarize(t.items, t.diff);
    let state;
    if (t.outcome) {
        state = t.outcome === 'ok' ? 'ok' : t.outcome === 'error' ? 'error' : 'cancelled';
    }
    else {
        state = hasAwaiting(t.items) ? 'awaiting-user' : 'streaming';
    }
    return { ...t, summary, state };
}
function hasAwaiting(items) {
    return items.some((it) => ((it.type === 'question' || it.type === 'approval') && it.state === 'awaiting-user') ||
        (it.type === 'subagent' && hasAwaiting(it.items)));
}
function diffStats(files) {
    let additions = 0;
    let deletions = 0;
    for (const f of files) {
        if (f.additions != null || f.deletions != null) {
            additions += f.additions ?? 0;
            deletions += f.deletions ?? 0;
            continue;
        }
        for (const h of f.hunks ?? []) {
            for (const l of h.lines) {
                if (l.startsWith('+'))
                    additions++;
                else if (l.startsWith('-'))
                    deletions++;
            }
        }
        if (!f.hunks && f.unified) {
            for (const l of f.unified.split('\n')) {
                if (l.startsWith('+') && !l.startsWith('+++'))
                    additions++;
                else if (l.startsWith('-') && !l.startsWith('---'))
                    deletions++;
            }
        }
    }
    return { additions, deletions };
}
export function summarize(items, turnDiff) {
    const s = emptySummary();
    const editFiles = new Map();
    const walk = (list, nested) => {
        for (const it of list) {
            if (it.type === 'tool') {
                s.tools++;
                switch (it.toolKind) {
                    case 'shell':
                        s.commands++;
                        break;
                    case 'edit':
                    case 'write':
                        s.edits++;
                        for (const d of it.diffs)
                            editFiles.set(d.path, d);
                        if (!it.diffs.length && it.path)
                            editFiles.set(it.path, { path: it.path, op: 'modify' });
                        break;
                    case 'read':
                        s.reads++;
                        break;
                    case 'search':
                        s.searches++;
                        break;
                    case 'web':
                        s.web++;
                        break;
                    case 'mcp':
                        s.mcp++;
                        break;
                    case 'image_gen':
                        s.images++;
                        break;
                }
                if (it.state === 'error')
                    s.errors++;
                if (it.state === 'streaming' && !it.background)
                    s.current =
                        it.toolKind === 'shell' && it.command ? parseCommand(it.command).title : it.title;
            }
            else if (it.type === 'subagent') {
                s.subagents++;
                if (it.state === 'error')
                    s.errors++;
                if (it.state === 'streaming')
                    s.current = it.description;
                walk(it.items, true);
            }
            else if (it.type === 'error' && !it.retrying && !nested) {
                s.errors++;
            }
        }
    };
    walk(items, false);
    const files = turnDiff ?? [...editFiles.values()];
    s.files = files.length;
    const st = diffStats(files);
    s.additions = st.additions;
    s.deletions = st.deletions;
    return s;
}
/**
 * The turn's final answer: the last top-level assistant text block, once no activity follows
 * it. While a turn is streaming and tools are still running after the last text, there is no
 * final answer yet (L0 shows the live status line instead).
 */
export function finalAnswer(turn) {
    for (let i = turn.items.length - 1; i >= 0; i--) {
        const it = turn.items[i];
        if (!it)
            continue;
        if (it.type === 'text')
            return it;
        if (turn.outcome)
            continue;
        if (it.type === 'tool' || it.type === 'subagent')
            return null;
    }
    if (turn.resultText) {
        return {
            type: 'text',
            id: `${turn.id}:result`,
            blockId: `${turn.id}:result`,
            seq: 0,
            ts: turn.endedAt ?? turn.startedAt,
            state: 'ok',
            text: turn.resultText,
            streaming: false,
            aborted: false,
        };
    }
    return null;
}
/** Items that stay visible at L0 regardless of disclosure. */
export function isPinned(it) {
    switch (it.type) {
        case 'question':
        case 'approval':
        case 'error':
            return true;
        case 'marker':
            // A repository change inside a turn is a side effect of the agent's own git command
            // (rebase, checkout, …): it stays with the collapsed commands, not as its own L0 row.
            return it.marker !== 'vcs_change';
        case 'compaction':
            return true;
        case 'notice':
            return it.level === 'warning';
        case 'subagent':
            return hasAwaiting(it.items);
        default:
            return false;
    }
}
const GROUPABLE = new Set(['read', 'search', 'web', 'mcp']);
/** Collapse runs of ≥2 consecutive same-kind read/search/web/mcp tools into one group row. */
export function groupActivity(items) {
    const out = [];
    let run = [];
    const flush = () => {
        const first = run[0];
        if (run.length >= 2 && first) {
            out.push({
                type: 'group',
                id: `group:${first.id}`,
                toolKind: first.toolKind,
                items: run,
                state: groupState(run),
            });
        }
        else
            out.push(...run);
        run = [];
    };
    let prev;
    for (const it of items) {
        // "Thought for 8ms" with nothing to show is noise; real thinking keeps its row.
        if (it.type === 'thinking' &&
            !it.streaming &&
            (it.redacted || !it.text.trim()) &&
            (it.durationMs ?? 0) < 1000)
            continue;
        const before = prev;
        prev = it;
        // The repository change a git command caused is already that command's row.
        if (it.type === 'marker' &&
            it.marker === 'vcs_change' &&
            before?.type === 'tool' &&
            before.toolKind === 'shell') {
            prev = before;
            continue;
        }
        if (it.type === 'tool' && GROUPABLE.has(it.toolKind) && it.images.length === 0) {
            const head = run[0];
            if (head && head.toolKind !== it.toolKind)
                flush();
            run.push(it);
            continue;
        }
        // Thinking between reads doesn't break a group visually at L1.
        if (it.type === 'thinking' && run.length) {
            flush();
            out.push(it);
            continue;
        }
        flush();
        out.push(it);
    }
    flush();
    return out;
}
export function groupState(items) {
    if (items.some((i) => i.state === 'awaiting-user'))
        return 'awaiting-user';
    if (items.some((i) => i.state === 'streaming' || i.state === 'pending'))
        return 'streaming';
    if (items.some((i) => i.state === 'error'))
        return 'error';
    if (items.every((i) => i.state === 'cancelled'))
        return 'cancelled';
    return 'ok';
}
/**
 * What a turn renders at a disclosure level (the user message is always rendered separately).
 *   L0: pinned items (questions, approvals, errors, markers) + the final answer + artifacts.
 *   L1/L2: every item in order with activity grouped; L2 differs only in expansion.
 */
export function visibleItems(turn, level) {
    if (level === 0) {
        const final = finalAnswer(turn);
        const out = turn.items.filter(isPinned);
        // While streaming, keep the live (last) text visible at L0 so the answer appears as it forms.
        if (final)
            out.push(final);
        else if (!turn.outcome) {
            const last = turn.items[turn.items.length - 1];
            if (last?.type === 'text')
                out.push(last);
        }
        // Evidence (captures, generated images) stays visible under the answer.
        out.push(...turn.items.filter((it) => it.type === 'artifact'));
        return out;
    }
    return groupActivity(turn.items);
}
/** Latest open turn defaults to L1 so live activity is visible; finished turns fold to L0. */
export function defaultLevel(turn, isLatest) {
    return isLatest && !turn.outcome ? 1 : 0;
}
export function turns(view) {
    const out = [];
    for (const x of view.entries)
        if (x.kind === 'turn')
            out.push(x.turn);
    return out;
}
/** Open questions/approvals with their turn — for pinning, the Inbox, and focus management. */
export function openAsks(view) {
    const out = [];
    const walk = (items) => {
        for (const it of items) {
            if ((it.type === 'question' || it.type === 'approval') && it.state === 'awaiting-user')
                out.push(it);
            if (it.type === 'subagent')
                walk(it.items);
        }
    };
    for (const t of turns(view))
        walk(t.items);
    return out;
}
/** Running subagents across the latest turn — for the live parallel status strip. */
export function runningSubagents(turn) {
    return turn.items.filter((it) => it.type === 'subagent' && it.state === 'streaming');
}
/** All file diffs the session produced (latest per path) — the Changes drawer. */
export function sessionChanges(view) {
    const byPath = new Map();
    for (const t of turns(view)) {
        const files = t.diff ??
            t.items.flatMap((it) => (it.type === 'tool' && it.toolKind !== 'read' ? it.diffs : []));
        for (const f of files)
            byPath.set(f.path, f);
    }
    return [...byPath.values()];
}
/**
 * Artifact completions may live inside subagents; all viewer surfaces use the same inventory.
 * A windowed view adds the artifacts of turns it has not loaded from the materialized state.
 */
export function artifactItems(view) {
    const result = [];
    function walk(items) {
        for (const item of items) {
            if (item.type === 'artifact')
                result.push(item);
            else if (item.type === 'subagent')
                walk(item.items);
        }
    }
    for (const turn of turns(view))
        walk(turn.items);
    const hidden = view.material?.artifactItems;
    if (!hidden)
        return result;
    const shown = new Set(result.map((a) => a.artifactId));
    return [...Object.values(hidden).filter((a) => !shown.has(a.artifactId)), ...result];
}
//# sourceMappingURL=model.js.map